import {
  Activity,
  Aperture,
  ArrowRight,
  CircleDot,
  Clock3,
  Compass,
  Crosshair,
  Eye,
  EyeOff,
  FileText,
  Filter,
  FolderOpen,
  FolderTree,
  Link2,
  Move3D,
  Palette,
  Pause,
  Play,
  Radio,
  RefreshCw,
  Search,
  Settings,
  ShieldCheck,
  Sparkles,
  Tags,
  X,
  Zap
} from 'lucide-react';
import { useEffect, useMemo, useState, type CSSProperties, type FormEvent } from 'react';
import type React from 'react';
import type { LanguagePreference, VaultConfigResponse, VaultEvent, VaultGraph, VaultNode } from '../../shared/types';
import type { GrowthState, NavigationMode } from '../App';
import type { TrailerWindowCue } from '../lib/capture-mode';
import type { Translate } from '../lib/i18n';
import { FloatingWindow } from './FloatingWindow';

export interface FilterState {
  query: string;
  areas: string[];
  tags: string[];
  statuses: string[];
  types: string[];
  showUnresolved: boolean;
}

type FacetKind = 'tag' | 'status' | 'type';

interface FacetOption {
  kind: FacetKind;
  value: string;
  count: number;
}

interface ChromeProps {
  graph: VaultGraph;
  events: VaultEvent[];
  connection: 'connecting' | 'live' | 'offline' | 'demo';
  filters: FilterState;
  selectedNode?: VaultNode;
  backlinks: VaultNode[];
  configState: VaultConfigResponse | null;
  configError?: string;
  configSaving: boolean;
  vaultDialogOpen: boolean;
  demoMode: boolean;
  language: LanguagePreference;
  t: Translate;
  navigationMode: NavigationMode;
  primaryLiveNodeId?: string;
  historyPlaying: boolean;
  growth: GrowthState;
  highlightsEnabled: boolean;
  labelsEnabled: boolean;
  trailerActiveWindow?: TrailerWindowCue;
  growthTotal: number;
  growthVisible: number;
  onFiltersChange: (filters: FilterState) => void;
  onSelectNode: (nodeId: string) => void;
  onVaultPathSubmit: (vaultPath: string) => Promise<boolean>;
  onVaultDialog: () => Promise<boolean>;
  onLanguageToggle: () => void;
  onNavigationModeChange: (mode: NavigationMode) => void;
  onGrowthChange: (growth: GrowthState) => void;
  onLabelsEnabledChange: (enabled: boolean) => void;
  onToggleHistory: () => void;
  onFocusEvent: (event: VaultEvent) => void;
  onFocusLive: () => void;
  onClearFocus: () => void;
}

export function Chrome({
  graph,
  events,
  connection,
  filters,
  selectedNode,
  backlinks,
  configState,
  configError,
  configSaving,
  vaultDialogOpen,
  demoMode,
  language,
  t,
  navigationMode,
  primaryLiveNodeId,
  historyPlaying,
  growth,
  highlightsEnabled,
  labelsEnabled,
  trailerActiveWindow,
  growthTotal,
  growthVisible,
  onFiltersChange,
  onSelectNode,
  onVaultPathSubmit,
  onVaultDialog,
  onLanguageToggle,
  onNavigationModeChange,
  onGrowthChange,
  onLabelsEnabledChange,
  onToggleHistory,
  onFocusEvent,
  onFocusLive,
  onClearFocus
}: ChromeProps) {
  const locale = language === 'de' ? 'de-DE' : 'en-US';
  const latestEvent = events.at(-1);
  const activeEvents = events
    .filter((event) => event.path && event.path !== '.' && event.type !== 'ready')
    .slice(-5)
    .reverse();
  const activeAreas = new Set(filters.areas);
  const activeFilterCount = filters.tags.length + filters.statuses.length + filters.types.length;
  const [vaultPathDraft, setVaultPathDraft] = useState('');
  const [facetQuery, setFacetQuery] = useState('');
  const [showVaultLaunch, setShowVaultLaunch] = useState(true);
  const currentVaultPath = configState?.config.vaultPath ?? '';
  const hasValidVault = Boolean(configState?.status.valid);
  const areaColorMap = useMemo(() => buildAreaColorMap(graph), [graph]);

  useEffect(() => {
    if (configState?.config.vaultPath) setVaultPathDraft(configState.config.vaultPath);
  }, [configState?.config.vaultPath]);

  function update(partial: Partial<FilterState>) {
    onFiltersChange({ ...filters, ...partial });
  }

  function toggleArea(area: string) {
    const next = new Set(activeAreas);
    if (next.has(area)) next.delete(area);
    else next.add(area);
    update({ areas: [...next] });
  }

  async function submitVaultPath(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await onVaultPathSubmit(vaultPathDraft);
  }

  function dismissVaultLaunch() {
    setShowVaultLaunch(false);
  }

  async function chooseVault() {
    const changed = await onVaultDialog();
    if (changed && configState?.config.vaultPath) setVaultPathDraft(configState.config.vaultPath);
    return changed;
  }

  async function chooseVaultFromLaunch() {
    const changed = await chooseVault();
    if (changed) dismissVaultLaunch();
  }

  return (
    <>
      {showVaultLaunch && !demoMode && (
        <VaultLaunchPrompt
          t={t}
          vaultPath={currentVaultPath}
          hasValidVault={hasValidVault}
          configError={configError}
          busy={vaultDialogOpen || configSaving}
          onChoose={() => void chooseVaultFromLaunch()}
          onContinue={dismissVaultLaunch}
        />
      )}
      <header className="topbar">
        <div className="brand-mark">
          <Sparkles size={18} />
          <span>{t('appTitle')}</span>
        </div>
        <div className="metric-strip">
          <Metric icon={<CircleDot size={16} />} label={t('nodes')} value={String(graph.nodes.length)} />
          <Metric icon={<Link2 size={16} />} label={t('links')} value={String(graph.stats.links)} />
          <Metric icon={<FileText size={16} />} label={t('files')} value={String(graph.stats.files)} />
          <Metric icon={<Clock3 size={16} />} label={t('index')} value={`${graph.stats.durationMs} ms`} />
        </div>
        <div className="topbar-actions">
          <button
            className="language-toggle"
            type="button"
            onClick={onLanguageToggle}
            title={language === 'de' ? 'Switch to English' : 'Auf Deutsch umschalten'}
          >
            {language === 'de' ? 'DE' : 'EN'}
          </button>
          <div className={`live-state live-state--${connection}`}>
            <Radio size={16} />
            <span>{connection === 'live' ? t('live') : connection === 'demo' ? t('demoMode') : connection === 'connecting' ? t('connecting') : t('offline')}</span>
          </div>
        </div>
      </header>

      <FloatingWindow
        id="controls"
        title={t('controlDeck')}
        icon={<Compass size={15} />}
        defaultRect={{ x: 18, y: 86, width: 326, height: Math.min(560, Math.max(460, window.innerHeight - 340)) }}
        minWidth={292}
        minHeight={320}
        className="control-panel"
        forcedCollapsed={trailerActiveWindow ? trailerActiveWindow !== 'controls' : undefined}
        emphasized={trailerActiveWindow === 'controls'}
      >
        <label className="search-field">
          <Search size={17} />
          <input value={filters.query} onChange={(event) => update({ query: event.target.value })} placeholder={t('searchPlaceholder')} />
        </label>

        <details className="fold-section is-primary" open>
          <summary>
            <Compass size={15} />
            <span>{t('view')}</span>
          </summary>
          <div className="nav-mode-grid">
            <NavModeButton icon={<Compass size={14} />} label={t('overview')} shortcut="A" active={navigationMode === 'overview'} onClick={() => onNavigationModeChange('overview')} />
            <NavModeButton icon={<Crosshair size={14} />} label={t('focus')} shortcut="S" active={navigationMode === 'focus'} onClick={() => onNavigationModeChange('focus')} />
            <NavModeButton icon={<Aperture size={14} />} label={t('depth')} shortcut="D" active={navigationMode === 'deep'} onClick={() => onNavigationModeChange('deep')} />
            <NavModeButton icon={<Move3D size={14} />} label={t('fly')} shortcut="F" active={navigationMode === 'fly'} onClick={() => onNavigationModeChange('fly')} />
          </div>
          {navigationMode === 'fly' && <p className="flight-hint">{t('flightHint')}</p>}
          <div className="focus-actions">
            <button className={labelsEnabled ? 'labels-button is-active' : 'labels-button'} type="button" onClick={() => onLabelsEnabledChange(!labelsEnabled)} title={t('keyLabelsDesc')}>
              {labelsEnabled ? <Tags size={15} /> : <EyeOff size={15} />}
              <span>{labelsEnabled ? t('labelsOn') : t('labelsOff')}</span>
              <kbd>R</kbd>
            </button>
            <button className="live-jump" type="button" disabled={!primaryLiveNodeId} onClick={onFocusLive} title={t('currentChange')}>
              <Zap size={15} />
              <span>{t('currentChange')}</span>
            </button>
            <button className={highlightsEnabled ? 'quiet-button' : 'quiet-button is-active'} type="button" onClick={onClearFocus} title={t('keyClearDesc')}>
              <X size={15} />
              <span>{t('clearFocus')}</span>
              <kbd>Q</kbd>
            </button>
          </div>
        </details>

        <details className="fold-section" open>
          <summary>
            <Palette size={15} />
            <span>{t('relations')}</span>
          </summary>
          <ColorLegend graph={graph} t={t} />
        </details>

        <details className="fold-section">
          <summary>
            <Settings size={15} />
            <span>{t('vault')}</span>
          </summary>
          {demoMode && (
            <div className="demo-mode-card">
              <div className="demo-mode-card__icon">
                <Sparkles size={18} />
              </div>
              <div>
                <strong>{t('demoVaultTitle')}</strong>
                <span>{t('demoVaultCopy')}</span>
              </div>
            </div>
          )}
          <div className="vault-selector-card">
            <div className="vault-selector-card__icon">
              <FolderOpen size={18} />
            </div>
            <div className="vault-selector-card__copy">
              <strong>{t('vaultSelectorTitle')}</strong>
              <span>{t('vaultSelectorCopy')}</span>
              <small title={currentVaultPath}>{currentVaultPath || t('pathPlaceholder')}</small>
            </div>
            <button className="vault-dialog-button" type="button" disabled={vaultDialogOpen || configSaving} onClick={() => void chooseVault()}>
              <span>{vaultDialogOpen ? t('chooseVaultBusy') : t('chooseVault')}</span>
              <ArrowRight size={14} />
            </button>
          </div>
          <form className="vault-path-form vault-path-form--manual" onSubmit={submitVaultPath}>
            <label className="vault-manual-label">
              <span>{t('manualPath')}</span>
              <input value={vaultPathDraft} onChange={(event) => setVaultPathDraft(event.target.value)} placeholder={t('pathPlaceholder')} spellCheck={false} />
            </label>
            <button className="vault-load-button" type="submit" disabled={configSaving} title={t('load')}>
              <RefreshCw size={15} />
              <span>{configSaving ? t('loading') : t('load')}</span>
            </button>
          </form>
          <div className={configState?.status.valid ? 'vault-path-state is-valid' : 'vault-path-state'}>
            <span>{configState?.status.message ?? configError ?? t('vaultChecking')}</span>
          </div>
          {configError && <p className="form-error">{configError}</p>}
        </details>

        <details className="fold-section">
          <summary>
            <FolderTree size={15} />
            <span>{t('areas')}</span>
          </summary>
          <div className="area-grid">
            {graph.areas
              .filter((area) => area !== 'Vault' && area !== 'Unresolved')
              .map((area) => (
                <button
                  key={area}
                  className={activeAreas.has(area) ? 'area-chip is-active' : 'area-chip'}
                  type="button"
                  title={area}
                  style={{ '--area-color': areaColorMap.get(area) ?? '#7dd3fc' } as CSSProperties}
                  onClick={() => toggleArea(area)}
                >
                  <span className="area-dot" />
                  <span>{area}</span>
                </button>
              ))}
          </div>
        </details>

        <details className="fold-section">
          <summary>
            <Filter size={15} />
            <span>{t('filters')}{activeFilterCount > 0 ? ` ${activeFilterCount}` : ''}</span>
          </summary>
          <SmartFilters graph={graph} filters={filters} facetQuery={facetQuery} t={t} onFacetQueryChange={setFacetQuery} onFiltersChange={onFiltersChange} />
          <button className="toggle-row" type="button" title={t('unresolved')} onClick={() => update({ showUnresolved: !filters.showUnresolved })}>
            {filters.showUnresolved ? <Eye size={16} /> : <EyeOff size={16} />}
            <span>{t('unresolved')}</span>
          </button>
        </details>

      </FloatingWindow>

      <FloatingWindow
        id="details"
        title={t('inspector')}
        icon={<Activity size={15} />}
        defaultRect={{ x: Math.max(356, window.innerWidth - 374), y: 86, width: 350, height: 360 }}
        minWidth={292}
        minHeight={260}
        className="detail-panel"
        forcedCollapsed={trailerActiveWindow ? trailerActiveWindow !== 'details' : undefined}
        emphasized={trailerActiveWindow === 'details'}
      >
        {selectedNode ? (
          <>
            <div className="detail-heading">
              <span className="kind">{selectedNode.kind}</span>
              <h1>{selectedNode.label}</h1>
              <p>{selectedNode.path || t('vault')}</p>
            </div>
            <div className="detail-grid">
              <Detail label={t('area')} value={selectedNode.area} />
              <Detail label={t('type')} value={selectedNode.type ?? '-'} />
              <Detail label={t('status')} value={selectedNode.status ?? '-'} />
              <Detail label={t('degree')} value={`${selectedNode.incoming} / ${selectedNode.outgoing}`} />
            </div>
            <div className="tag-row">
              {selectedNode.tags.slice(0, 12).map((tag) => (
                <span key={tag}>{tag}</span>
              ))}
            </div>
            <section className="panel-section">
              <div className="section-title">
                <Activity size={15} />
                <span>{t('backlinks')}</span>
              </div>
              <div className="backlink-list">
                {backlinks.slice(0, 18).map((node, index) => (
                  <button key={`${node.id}:${index}`} type="button" onClick={() => onSelectNode(node.id)}>
                    <span>{node.label}</span>
                    <small>{node.area}</small>
                  </button>
                ))}
                {backlinks.length === 0 && <p className="empty-copy">{t('noBacklinks')}</p>}
              </div>
            </section>
          </>
        ) : highlightsEnabled ? (
          <div className="live-focus">
            <div className="live-focus-orbit">
              <Zap size={30} />
            </div>
            <span className="kind">{t('liveFocus')}</span>
            <h1>{activeEvents[0]?.type === 'add' ? t('newFile') : t('currentChange')}</h1>
            <p>{activeEvents[0]?.path ?? latestEvent?.path ?? t('vault')}</p>
            <div className="live-focus-list">
              {activeEvents.map((event, index) => (
                <button key={`${event.id}:${index}`} type="button" onClick={() => onSelectNode(`file:${event.path}`)}>
                  <span>{event.type}</span>
                  <strong>{event.path}</strong>
                  <time>{new Date(event.at).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</time>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="empty-detail">
            <div className="empty-orbit">
              <CircleDot size={26} />
            </div>
            <span className="kind">{t('calmView')}</span>
            <h1>{t('noFileSelected')}</h1>
            <p>{t('noHighlightCopy')}</p>
          </div>
        )}
      </FloatingWindow>

      <Timeline
        events={events}
        historyPlaying={historyPlaying}
        growth={growth}
        growthTotal={growthTotal}
        growthVisible={growthVisible}
        highlightsEnabled={highlightsEnabled}
        labelsEnabled={labelsEnabled}
        language={language}
        navigationMode={navigationMode}
        t={t}
        onGrowthChange={onGrowthChange}
        onLabelsEnabledChange={onLabelsEnabledChange}
        onToggleHistory={onToggleHistory}
        onFocusEvent={onFocusEvent}
        onClearFocus={onClearFocus}
        onNavigationModeChange={onNavigationModeChange}
        trailerActiveWindow={trailerActiveWindow}
      />
    </>
  );
}

function VaultLaunchPrompt({
  t,
  vaultPath,
  hasValidVault,
  configError,
  busy,
  onChoose,
  onContinue
}: {
  t: Translate;
  vaultPath: string;
  hasValidVault: boolean;
  configError?: string;
  busy: boolean;
  onChoose: () => void;
  onContinue: () => void;
}) {
  return (
    <div className="vault-launch" role="presentation">
      <section className="vault-launch__panel" role="dialog" aria-modal="true" aria-labelledby="vault-launch-title">
        <div className="vault-launch__header">
          <div className="vault-launch__mark" aria-hidden="true">
            <Sparkles size={22} />
          </div>
          <span>{t('appTitle')}</span>
        </div>
        <div className="vault-launch__copy">
          <h1 id="vault-launch-title">{t('vaultLaunchTitle')}</h1>
          <p className="vault-launch__subtitle">{t('vaultLaunchSubtitle')}</p>
          <p>{t('vaultLaunchCopy')}</p>
        </div>
        <div className="vault-launch__current">
          <FolderOpen size={16} />
          <span title={vaultPath}>{vaultPath || t('pathPlaceholder')}</span>
        </div>
        <div className="vault-launch__trust">
          <ShieldCheck size={16} />
          <div>
            <strong>{t('vaultLaunchLocal')}</strong>
            <span>{t('vaultLaunchLocalCopy')}</span>
          </div>
        </div>
        <div className="vault-launch__actions">
          <button className="vault-launch__primary" type="button" disabled={busy} onClick={onChoose}>
            <FolderOpen size={17} />
            <span>{busy ? t('chooseVaultBusy') : t('vaultLaunchImport')}</span>
            <ArrowRight size={15} />
          </button>
          <button className="vault-launch__secondary" type="button" onClick={onContinue}>
            {hasValidVault ? t('vaultLaunchContinue') : t('vaultLaunchManualAction')}
          </button>
        </div>
        <p className="vault-launch__hint">{t('vaultLaunchManualHint')}</p>
        {configError && <p className="form-error">{configError}</p>}
      </section>
    </div>
  );
}

function ColorLegend({ graph, t }: { graph: VaultGraph; t: Translate }) {
  const areaColors = useMemo(() => {
    const buckets = new Map<string, { color: string; count: number }>();
    for (const node of graph.nodes) {
      if (node.area === 'Vault' || node.kind === 'unresolved') continue;
      const current = buckets.get(node.area) ?? { color: node.color, count: 0 };
      current.count += 1;
      buckets.set(node.area, current);
    }
    return [...buckets.entries()]
      .map(([area, bucket]) => ({ area, ...bucket }))
      .sort((a, b) => b.count - a.count || a.area.localeCompare(b.area, 'de'))
      .slice(0, 7);
  }, [graph.nodes]);

  return (
    <div className="legend-list">
      {areaColors.map((entry) => (
        <div className="legend-row" key={entry.area}>
          <span className="legend-swatch" style={{ background: entry.color, boxShadow: `0 0 14px ${entry.color}88` }} />
          <strong>{entry.area}</strong>
          <small>{entry.count}</small>
        </div>
      ))}
      <div className="legend-rule" />
      <LegendRow swatch="legend-swatch--live" label={t('colorLive')} detail={t('colorEdit')} />
      <LegendRow swatch="legend-swatch--birth" label={t('colorBirth')} detail={t('colorGenesis')} />
      <LegendRow swatch="legend-swatch--link" label={t('colorLinks')} detail={t('colorWiki')} />
      <LegendRow swatch="legend-swatch--property" label={t('colorProperty')} detail={t('colorRelation')} />
      <LegendRow swatch="legend-swatch--unresolved" label={t('colorUnresolved')} detail={t('colorOpen')} />
    </div>
  );
}

function LegendRow({ swatch, label, detail }: { swatch: string; label: string; detail: string }) {
  return (
    <div className="legend-row">
      <span className={`legend-swatch ${swatch}`} />
      <strong>{label}</strong>
      <small>{detail}</small>
    </div>
  );
}

function buildAreaColorMap(graph: VaultGraph): Map<string, string> {
  const colors = new Map<string, string>();
  for (const node of graph.nodes) {
    if (node.kind === 'unresolved') continue;
    if (!colors.has(node.area)) colors.set(node.area, node.color);
  }
  return colors;
}

function SmartFilters({
  graph,
  filters,
  facetQuery,
  t,
  onFacetQueryChange,
  onFiltersChange
}: {
  graph: VaultGraph;
  filters: FilterState;
  facetQuery: string;
  t: Translate;
  onFacetQueryChange: (value: string) => void;
  onFiltersChange: (filters: FilterState) => void;
}) {
  const selected = useMemo(
    () => [
      ...filters.tags.map((value) => ({ kind: 'tag' as const, value })),
      ...filters.statuses.map((value) => ({ kind: 'status' as const, value })),
      ...filters.types.map((value) => ({ kind: 'type' as const, value }))
    ],
    [filters.statuses, filters.tags, filters.types]
  );
  const options = useMemo(() => buildFacetOptions(graph), [graph]);
  const query = facetQuery.trim().toLowerCase();
  const visibleOptions = options
    .filter((option) => !isFacetSelected(filters, option) && (!query || `${facetLabel(option.kind, t)} ${option.value}`.toLowerCase().includes(query)))
    .slice(0, query ? 18 : 12);

  function toggleFacet(option: Pick<FacetOption, 'kind' | 'value'>) {
    const key = facetKey(option.kind);
    const current = new Set(filters[key]);
    if (current.has(option.value)) current.delete(option.value);
    else current.add(option.value);
    onFiltersChange({ ...filters, [key]: [...current] });
  }

  return (
    <div className="smart-filter">
      <label className="facet-search">
        <Search size={14} />
        <input value={facetQuery} onChange={(event) => onFacetQueryChange(event.target.value)} placeholder="Tags, Status, Type" />
      </label>
      {selected.length > 0 && (
        <div className="selected-facets">
          {selected.map((option) => (
            <button key={`${option.kind}:${option.value}`} type="button" onClick={() => toggleFacet(option)}>
              <span>{facetLabel(option.kind, t)}</span>
              <strong>{option.value}</strong>
              <X size={12} />
            </button>
          ))}
        </div>
      )}
      <div className="facet-options">
        {visibleOptions.map((option) => (
          <button key={`${option.kind}:${option.value}`} type="button" onClick={() => toggleFacet(option)}>
            <span>{facetLabel(option.kind, t)}</span>
            <strong>{option.value}</strong>
            <small>{option.count}</small>
          </button>
        ))}
        {visibleOptions.length === 0 && <p className="empty-copy">-</p>}
      </div>
    </div>
  );
}

function buildFacetOptions(graph: VaultGraph): FacetOption[] {
  const counts = new Map<string, FacetOption>();
  for (const node of graph.nodes) {
    for (const tag of node.tags) addFacet(counts, 'tag', tag);
    if (node.status) addFacet(counts, 'status', node.status);
    if (node.type) addFacet(counts, 'type', node.type);
  }
  return [...counts.values()].sort((a, b) => b.count - a.count || a.value.localeCompare(b.value, 'de'));
}

function addFacet(counts: Map<string, FacetOption>, kind: FacetKind, value: string) {
  const key = `${kind}:${value}`;
  const current = counts.get(key);
  if (current) current.count += 1;
  else counts.set(key, { kind, value, count: 1 });
}

function isFacetSelected(filters: FilterState, option: Pick<FacetOption, 'kind' | 'value'>) {
  return filters[facetKey(option.kind)].includes(option.value);
}

function facetKey(kind: FacetKind): 'tags' | 'statuses' | 'types' {
  if (kind === 'tag') return 'tags';
  if (kind === 'status') return 'statuses';
  return 'types';
}

function facetLabel(kind: FacetKind, t: Translate) {
  if (kind === 'tag') return 'Tag';
  if (kind === 'status') return t('status');
  return t('type');
}

function Metric({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="metric">
      {icon}
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function NavModeButton({ icon, label, shortcut, active, onClick }: { icon: React.ReactNode; label: string; shortcut: string; active: boolean; onClick: () => void }) {
  return (
    <button className={active ? 'nav-mode is-active' : 'nav-mode'} type="button" onClick={onClick}>
      {icon}
      <span>{label}</span>
      <kbd>{shortcut}</kbd>
    </button>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function Timeline({
  events,
  historyPlaying,
  growth,
  growthTotal,
  growthVisible,
  highlightsEnabled,
  labelsEnabled,
  language,
  navigationMode,
  t,
  onGrowthChange,
  onLabelsEnabledChange,
  onToggleHistory,
  onFocusEvent,
  onClearFocus,
  onNavigationModeChange,
  trailerActiveWindow
}: {
  events: VaultEvent[];
  historyPlaying: boolean;
  growth: GrowthState;
  growthTotal: number;
  growthVisible: number;
  highlightsEnabled: boolean;
  labelsEnabled: boolean;
  language: LanguagePreference;
  navigationMode: NavigationMode;
  t: Translate;
  onGrowthChange: (growth: GrowthState) => void;
  onLabelsEnabledChange: (enabled: boolean) => void;
  onToggleHistory: () => void;
  onFocusEvent: (event: VaultEvent) => void;
  onClearFocus: () => void;
  onNavigationModeChange: (mode: NavigationMode) => void;
  trailerActiveWindow?: TrailerWindowCue;
}) {
  const visibleEvents = events.slice(-16).reverse();
  const growthPercent = Math.round(growth.progress * 100);
  const locale = language === 'de' ? 'de-DE' : 'en-US';

  return (
    <FloatingWindow
      id="timeline"
      title={t('timeline')}
      icon={<Activity size={15} />}
      defaultRect={{ x: 18, y: Math.max(560, window.innerHeight - 218), width: Math.min(340, Math.max(320, window.innerWidth * 0.23)), height: 206 }}
      minWidth={320}
      minHeight={188}
      className="timeline"
      forcedCollapsed={trailerActiveWindow ? trailerActiveWindow !== 'timeline' : undefined}
      emphasized={trailerActiveWindow === 'timeline'}
    >
      <div className="timeline-head">
        <div className="timeline-actions timeline-actions--primary">
          <button
            type="button"
            onClick={() => onGrowthChange({ ...growth, enabled: true, playing: growth.enabled ? !growth.playing : true, progress: !growth.enabled || growth.progress >= 1 ? 0.04 : growth.progress })}
            title={t('genesis')}
          >
            {growth.enabled && growth.playing ? <Pause size={14} /> : <Play size={14} />}
            <strong>{growth.enabled ? t('genesis') : t('genesis')}</strong>
          </button>
          <button type="button" onClick={onToggleHistory} title={t('eventTour')}>
            {historyPlaying ? <Pause size={14} /> : <Play size={14} />}
            <strong>{historyPlaying ? t('pause') : t('eventTour')}</strong>
          </button>
          <button className={navigationMode === 'fly' ? 'timeline-fly-toggle is-active' : 'timeline-fly-toggle'} type="button" onClick={() => onNavigationModeChange('fly')} title={t('fly')}>
            <Move3D size={14} />
            <strong>{t('fly')}</strong>
            <kbd>F</kbd>
          </button>
        </div>
        <div className="timeline-range-stack">
          <label className={growth.enabled ? 'growth-scrubber is-active' : 'growth-scrubber'}>
            <small>{growth.enabled ? `${growthVisible}/${growthTotal} ${t('notes')} · ${growthPercent}%` : t('fullVault')}</small>
            <input type="range" min="0.02" max="1" step="0.01" value={growth.progress} onChange={(event) => onGrowthChange({ ...growth, enabled: true, playing: false, progress: Number(event.target.value) })} />
          </label>
          <label className="speed-control">
            <span>{t('speed')} {growth.speed.toFixed(1)}x</span>
            <input type="range" min="0.2" max="4" step="0.2" value={growth.speed} onChange={(event) => onGrowthChange({ ...growth, speed: Number(event.target.value) })} />
          </label>
        </div>
        <div className="timeline-actions timeline-actions--toggles">
          <button className={growth.focusZoom ? 'focus-zoom-toggle is-active' : 'focus-zoom-toggle'} type="button" onClick={() => onGrowthChange({ ...growth, focusZoom: !growth.focusZoom })} title={growth.focusZoom ? t('focusZoomOn') : t('focusZoomOff')}>
            <Crosshair size={14} />
            <strong>{growth.focusZoom ? t('focusZoomOn') : t('focusZoomOff')}</strong>
          </button>
          <button className={labelsEnabled ? 'timeline-label-toggle is-active' : 'timeline-label-toggle'} type="button" onClick={() => onLabelsEnabledChange(!labelsEnabled)} title={t('keyLabelsDesc')}>
            {labelsEnabled ? <Tags size={14} /> : <EyeOff size={14} />}
            <strong>{labelsEnabled ? t('labelsOn') : t('labelsOff')}</strong>
            <kbd>R</kbd>
          </button>
          <button className={highlightsEnabled ? 'timeline-quiet-toggle' : 'timeline-quiet-toggle is-active'} type="button" onClick={onClearFocus} title={t('clearFocus')}>
            <X size={14} />
            <strong>{t('clearFocus')}</strong>
            <kbd>Q</kbd>
          </button>
        </div>
      </div>
      <div className="timeline-track">
        {visibleEvents.map((event, index) => (
          <button type="button" key={`${event.id}:${index}`} className={`timeline-event timeline-event--${event.type}${index === 0 ? ' is-current' : ''}`} onClick={() => onFocusEvent(event)} title={t('focus')}>
            <span>{event.type}</span>
            <strong>{event.path}</strong>
            <time>{new Date(event.at).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</time>
          </button>
        ))}
      </div>
    </FloatingWindow>
  );
}
